import styles from "./AISupportPanel.module.scss";
import Chat from "@medoma/components/Chat/Chat";
import { useRef, useState, lazy } from "react";
import { useLingui, Trans } from "@lingui/react/macro";
import { useChatScroll } from "@medoma/chat/useChatScroll";
import clsx from "clsx";
import { PlainButton } from "@medoma/components/Button/Button";
import BinIcon from "@medoma/components/icons/BinIcon";
import { Text } from "@medoma/components/Text/Text";
import {
  aiSupportChatMessageSchema,
  sendMessageToSession,
  startNewSession,
} from "@/api/AISupportAgent";
import AISupportSentryFeedback from "./AISupportSentryFeedback/AISupportSentryFeedback";
import { useMutation } from "@tanstack/react-query";
import type { IAISupportChatMessage } from "@/api/AISupportAgent";
import { deducedError } from "@/Utils/ErrorUtils";
import { useSessionStorage } from "@/Utils/useSessionStorage";
import { useUserProfile } from "@/api/Users";
import { z } from "zod";

// Heavy dependency, lazy load it
const ReactMarkdown = lazy(() => import("react-markdown"));

export const AISupportPanel = () => {
  const { aiSupportChatMessages, saveMessages, clearChat } = useAISupportChat();
  const [inputValue, setInputValue] = useState("");
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const messagePanelRef = useRef<HTMLDivElement>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const { scrollMessagePanelToBottom } = useChatScroll({
    messagePanelRef: messagePanelRef,
    inputTextAreaRef: inputTextAreaRef,
    messages: aiSupportChatMessages,
  });
  const { t } = useLingui();

  const {
    mutate: sendMessage,
    isPending,
    isError,
    error,
  } = useMutation({
    mutationFn: ({ userMessage }: { userMessage: IAISupportChatMessage }) => {
      // If new session, start a new session. Otherwise, send message to existing session.
      if (!userMessage.sessionId) {
        return startNewSession({ message: userMessage.text });
      } else {
        return sendMessageToSession({
          sessionId: userMessage.sessionId,
          message: userMessage.text,
        });
      }
    },
    onSuccess: (response) => {
      const aiResponse: IAISupportChatMessage = {
        id: `ai-${Date.now()}`,
        sessionId: response.sessionId,
        text: response.message,
        type: "ai",
      };

      // Populate any null sessionId with the response sessionId
      const updatedMessages = aiSupportChatMessages.map((msg) =>
        msg.sessionId === null
          ? { ...msg, sessionId: response.sessionId }
          : msg,
      );

      saveMessages([...updatedMessages, aiResponse]);
    },
  });

  const validateAndSendMessage = () => {
    setFeedbackSent(false);
    if (!inputValue.trim()) {
      return;
    }

    const id = `user-${Date.now()}`;
    const sessionId = aiSupportChatMessages[0]?.sessionId ?? null;

    const userMessage: IAISupportChatMessage = {
      id,
      sessionId,
      text: inputValue.trim(),
      type: "user",
    };

    saveMessages([...aiSupportChatMessages, userMessage]);
    setInputValue("");

    sendMessage({ userMessage });
  };

  const onClearChat = () => {
    clearChat();
    setFeedbackSent(false);
  };

  const showFeedback =
    !isPending &&
    aiSupportChatMessages.some((message) => message.type === "ai");

  return (
    <div className={styles.chatContainer}>
      <Chat>
        {aiSupportChatMessages.length > 0 ? (
          <Chat.InfoPanel type="info" placement="top">
            <PlainButton onClick={onClearChat} weight="light">
              <BinIcon />
              <Trans>Rensa innehåll</Trans>
            </PlainButton>
          </Chat.InfoPanel>
        ) : null}
        <div
          className={styles.chatMessagePanel}
          ref={messagePanelRef}
          role="log"
          aria-live="polite"
        >
          <Text element="p" color="faded">
            <Trans>
              Handboken är en AI-agent med tillgång till en kunskapsbank om
              funktioner och arbetsflöden i Medomas plattform. Ställ en fråga så
              svarar den så bra som möjligt.
            </Trans>
          </Text>
          {aiSupportChatMessages.map((message: IAISupportChatMessage) => (
            <div
              key={message.id}
              className={clsx(
                styles.message,
                styles[`sender-${message.type}`],
                styles.markdown,
              )}
            >
              <ReactMarkdown>{message.text}</ReactMarkdown>
            </div>
          ))}
        </div>
        {isPending && (
          <Chat.InfoPanel type="info">
            <Chat.IsTyping text={t`Handboken tänker`} />
          </Chat.InfoPanel>
        )}
        {isError && (
          <Chat.InfoPanel type="error">
            <Text element="span">{deducedError(error)}</Text>
          </Chat.InfoPanel>
        )}
        {showFeedback && (
          <Chat.InfoPanel type="info">
            <AISupportSentryFeedback
              sessionId={aiSupportChatMessages[0]?.sessionId ?? null}
              feedbackSent={feedbackSent}
              setFeedbackSent={setFeedbackSent}
            />
          </Chat.InfoPanel>
        )}
        <Chat.InputPanel>
          <Chat.InputTextArea
            inputTextAreaRef={inputTextAreaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            onFocus={scrollMessagePanelToBottom}
            placeholder={t`Skriv till Handboken (utan namn eller känsliga personuppgifter)`}
          />
          <Chat.SendMessageButton
            aria-label={t`Skicka`}
            onClick={validateAndSendMessage}
            disabled={!inputValue.trim() || isPending}
          />
        </Chat.InputPanel>
      </Chat>
    </div>
  );
};

const useAISupportChat = () => {
  const { userId } = useUserProfile();
  const [aiSupportChatMessages, setAiSupportChatMessages] = useSessionStorage({
    key: "aiSupport.chatMessages",
    userId,
    initialValue: "[]",
  });

  // If the messages are not valid, return an empty array
  const safeParsedMessagesResult = z
    .array(aiSupportChatMessageSchema)
    .safeParse(JSON.parse(aiSupportChatMessages));

  const parsedMessages = safeParsedMessagesResult.success
    ? safeParsedMessagesResult.data
    : [];

  function saveMessages(messages: IAISupportChatMessage[]) {
    const stringifiedMessages = JSON.stringify(messages);
    setAiSupportChatMessages(stringifiedMessages);
  }

  function clearChat() {
    setAiSupportChatMessages("[]");
  }

  return {
    aiSupportChatMessages: parsedMessages,
    saveMessages,
    clearChat,
  } as const;
};
